/**
 * ロールに関する共通ユーティリティ。
 * ロール値そのものは DB（profiles.role）で管理し、ここでは表示ラベルと判定を集約する。
 *
 * - welfare_user       : 各事業所の利用者（福祉サービスを受ける人）
 * - welfare_staff      : 各事業所の職員（福祉サービスを提供する人・現場中心）
 * - welfare_admin      : 各事業所の管理者（自事業所の管理メニュー全部）
 * - welfare_supervisor : 複数の事業所をまとめて管轄する管理者（エリアマネージャー）
 * - general_user       : 福祉サービスを受けない一般の社員・スタッフ
 * - general_admin      : 全体を横断して管理する最上位（総合管理者）
 */

export type AppRole =
  | 'welfare_user'
  | 'welfare_staff'
  | 'welfare_admin'
  | 'welfare_supervisor'
  | 'general_user'
  | 'general_admin';

export const ROLE_LABEL: Record<string, string> = {
  general_admin: '総合管理者',
  welfare_supervisor: '福祉事業管轄',
  welfare_admin: '事業所管理者',
  welfare_staff: '職員',
  welfare_user: '利用者',
  general_user: '一般社員',
};

/** ロールの表示ラベルを返す。 */
export const getAppRoleLabel = (role?: string | null): string => {
  const normalized = normalizeAppRole(role);
  return (normalized ? ROLE_LABEL[normalized] : undefined) ?? '利用者';
};

/**
 * ロール再編（旧→新）の移行途中でも判定がぶれないよう、旧ロール値を新ロール値へ正規化する。
 * DB の正規化（`current_user_role()`）と `ai-manual-qa` は既に旧→新を吸収しており、
 * フロントだけ生値を見て食い違う（例: 総合管理者が「利用者」判定になる）のを防ぐ。
 * - user        -> welfare_user
 * - admin       -> welfare_staff
 * - super_admin -> general_admin
 */
export const normalizeAppRole = (role?: string | null): AppRole | string => {
  switch (role) {
    case 'user':
      return 'welfare_user';
    case 'admin':
      return 'welfare_staff';
    case 'super_admin':
      return 'general_admin';
    default:
      return role ?? '';
  }
};

/** 総合管理者（全体を横断管理する最上位）か。 */
export const isGeneralAdmin = (role?: string | null): boolean =>
  normalizeAppRole(role) === 'general_admin';

/** 福祉事業管轄（複数事業所を管轄するエリアマネージャー）か。 */
export const isWelfareSupervisor = (role?: string | null): boolean =>
  normalizeAppRole(role) === 'welfare_supervisor';

/** 事業所管理者（自事業所の管理メニューを持つ）か。 */
export const isWelfareAdmin = (role?: string | null): boolean =>
  normalizeAppRole(role) === 'welfare_admin';

/** 職員（現場中心の福祉スタッフ）か。 */
export const isWelfareStaff = (role?: string | null): boolean =>
  normalizeAppRole(role) === 'welfare_staff';

/** 自事業所のデータを扱える福祉ロール（職員・事業所管理者）か。 */
export const isOfficeRole = (role?: string | null): boolean => {
  const normalized = normalizeAppRole(role);
  return normalized === 'welfare_staff' || normalized === 'welfare_admin';
};

/** 利用者系のロール（福祉利用者・一般社員）か。 */
export const isEndUserRole = (role?: string | null): boolean => {
  const normalized = normalizeAppRole(role);
  return normalized === 'welfare_user' || normalized === 'general_user';
};

/** 全事業所を横断して扱えるロールか（総合管理者・福祉事業管轄）。 */
export const canSeeAllOffices = (role?: string | null): boolean => {
  const normalized = normalizeAppRole(role);
  return normalized === 'general_admin' || normalized === 'welfare_supervisor';
};

/** 管理系の権限を持つロールか（職員・事業所管理者・福祉事業管轄・総合管理者）。 */
export const isAdminLikeRole = (role?: string | null): boolean => {
  const normalized = normalizeAppRole(role);
  return (
    normalized === 'welfare_staff' ||
    normalized === 'welfare_admin' ||
    normalized === 'welfare_supervisor' ||
    normalized === 'general_admin'
  );
};

/** 事業所カレンダー（営業時間・休業日・半日営業日）を編集できるロールか。
 * 事業所管理者・福祉事業管轄・総合管理者のみ（職員は閲覧のみ）。 */
export const canEditOfficeCalendar = (role?: string | null): boolean => {
  const normalized = normalizeAppRole(role);
  return (
    normalized === 'welfare_admin' ||
    normalized === 'welfare_supervisor' ||
    normalized === 'general_admin'
  );
};

/** ユーザー管理・組織管理を扱えるロールか（事業所管理者・福祉事業管轄・総合管理者）。 */
export const canManageUsers = (role?: string | null): boolean => {
  const normalized = normalizeAppRole(role);
  return (
    normalized === 'welfare_admin' ||
    normalized === 'welfare_supervisor' ||
    normalized === 'general_admin'
  );
};